/**
 * The orcaloops sign-up door: a v5 {@link Door} card driven by Clerk's
 * headless sign-up API (`useSignUp`) instead of Clerk's `<SignUp>` UI.
 *
 * Two steps. `form`: email + password (the instance requires both, the
 * password at 15 characters or more), Clerk's CAPTCHA mount point, and
 * "Continue with Google". `code`: the six-digit code Clerk emails to verify
 * the address; a `complete` sign-up activates its session and lands the person
 * on the door's redirect.
 *
 * The instance has bot protection on, so `<div id="clerk-captcha" />` is in the
 * DOM on every `form` render — Clerk mounts its widget there when
 * `signUp.create` runs, and fails the call when it is missing.
 *
 * Clerk's promise-returning calls are lifted through {@link clerkTryPromise}
 * and the flows run as `Atom.fn` setters — no `async`/`await`, no `useState`,
 * no `useEffect`.
 *
 * @since 1.0.0
 */
import { useSignUp } from "@clerk/tanstack-react-start";
import { Atom, useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { useNavigate } from "@tanstack/react-router";
import { Effect, Match } from "effect";
import { type FormEvent, type ReactNode, useMemo } from "react";

import { clerkTryPromise } from "~/lib/clerk-effect.ts";
import type { LoginSearch } from "~/lib/login-search.ts";
import {
  clerkErrorMessage,
  DOOR_STRATEGY,
  doorRedirect,
  GOOGLE_STRATEGY,
  oauthRedirectUrls,
} from "~/lib/orcaloops-clerk.ts";

import { Door, OAuthButton } from "./Door.tsx";
import { Field, Input } from "./Form.tsx";
import { OrcaloopsFrame, ThemeToggle, TopCrumbs, TopGrow, TopMark } from "./OrcaloopsFrame.tsx";
import { Btn } from "./primitives.tsx";

/** The loaded branch of `useSignUp()`, derived so it tracks Clerk's own types. */
type LoadedSignUp = Extract<ReturnType<typeof useSignUp>, { readonly isLoaded: true }>;

/**
 * The slice of Clerk's sign-up resource the door drives, as `useSignUp()`
 * hands it over once loaded.
 *
 * @since 1.0.0
 */
export type SignUpResource = Pick<
  LoadedSignUp["signUp"],
  "create" | "prepareEmailAddressVerification" | "attemptEmailAddressVerification" | "authenticateWithRedirect"
>;

/** Clerk's `setActive`, as `useSignUp()` hands it over once loaded. @since 1.0.0 */
export type SignUpSetActive = LoadedSignUp["setActive"];

/** The door's two steps. @since 1.0.0 */
export type SignUpStep = "form" | "code";

/** The atom setters a flow drives. @since 1.0.0 */
export interface SignUpDoorSetters {
  readonly busy: (busy: boolean) => void;
  readonly error: (error: string | null) => void;
  readonly step: (step: SignUpStep) => void;
}

/** What every sign-up flow closes over. @since 1.0.0 */
export interface SignUpDoorDeps {
  readonly navigate: ReturnType<typeof useNavigate>;
  readonly search: LoginSearch;
  readonly set: SignUpDoorSetters;
  readonly setActive: SignUpSetActive;
  readonly signUp: SignUpResource;
}

/** The one-line error when Clerk abandoned the sign-up. @since 1.0.0 */
export const SIGN_UP_ABANDONED = "Start again.";

/**
 * Run one Clerk step with the busy flag raised and the error cleared; a
 * rejection anywhere in it becomes the door's error line.
 */
const guarded = <E extends { readonly cause: unknown }>(
  deps: SignUpDoorDeps,
  step: Effect.Effect<void, E>,
): Effect.Effect<void> =>
  Effect.sync(() => {
    deps.set.busy(true);
    deps.set.error(null);
  }).pipe(
    Effect.flatMap(() => step),
    Effect.catchAll((error) => Effect.sync(() => deps.set.error(clerkErrorMessage(error.cause)))),
    Effect.ensuring(Effect.sync(() => deps.set.busy(false))),
  );

/**
 * Create the sign-up from email + password, send the email code, and move to
 * the `code` step.
 *
 * @since 1.0.0
 */
export const startSignUp = (
  deps: SignUpDoorDeps,
  input: { readonly email: string; readonly password: string },
): Effect.Effect<void> =>
  guarded(
    deps,
    clerkTryPromise(() => deps.signUp.create({ emailAddress: input.email, password: input.password })).pipe(
      Effect.flatMap(() =>
        clerkTryPromise(() => deps.signUp.prepareEmailAddressVerification({ strategy: DOOR_STRATEGY }))
      ),
      Effect.flatMap(() => Effect.sync(() => deps.set.step("code"))),
    ),
  );

/**
 * Verify the emailed code. `complete` activates the new session and lands the
 * person on the door's redirect; `missing_requirements` names what Clerk still
 * needs; anything else (`abandoned`) asks them to start again.
 *
 * @since 1.0.0
 */
export const verifySignUpCode = (deps: SignUpDoorDeps, code: string): Effect.Effect<void> =>
  guarded(
    deps,
    clerkTryPromise(() => deps.signUp.attemptEmailAddressVerification({ code })).pipe(
      Effect.flatMap((result) =>
        Match.value(result.status).pipe(
          Match.when("complete", () =>
            clerkTryPromise(() => deps.setActive({ session: result.createdSessionId })).pipe(
              Effect.flatMap(() => clerkTryPromise(() => deps.navigate({ to: doorRedirect(deps.search) }))),
            )),
          Match.when(
            "missing_requirements",
            () => Effect.sync(() => deps.set.error(`Still needed: ${result.missingFields.join(", ")}`)),
          ),
          Match.orElse(() => Effect.sync(() => deps.set.error(SIGN_UP_ABANDONED))),
        )
      ),
    ),
  );

/**
 * Hand the browser to Google; the round-trip returns through
 * `/sign-up/sso-callback`.
 *
 * @since 1.0.0
 */
export const startGoogleSignUp = (deps: SignUpDoorDeps): Effect.Effect<void> =>
  guarded(
    deps,
    clerkTryPromise(() =>
      deps.signUp.authenticateWithRedirect({
        strategy: GOOGLE_STRATEGY,
        ...oauthRedirectUrls("sign-up", deps.search),
      })
    ),
  );

/** The flows as atom setters, run through the effect-atom runtime. @since 1.0.0 */
export const startSignUpFn = Atom.fn(
  (args: { readonly deps: SignUpDoorDeps; readonly email: string; readonly password: string }) =>
    startSignUp(args.deps, args),
);
/** @since 1.0.0 */
export const verifySignUpCodeFn = Atom.fn((args: { readonly code: string; readonly deps: SignUpDoorDeps }) =>
  verifySignUpCode(args.deps, args.code)
);
/** @since 1.0.0 */
export const startGoogleSignUpFn = Atom.fn((deps: SignUpDoorDeps) => startGoogleSignUp(deps));

/**
 * The card and its two steps. See the module doc.
 *
 * @since 1.0.0
 */
export function SignUpDoor({ search }: { readonly search: LoginSearch }): ReactNode {
  const clerk = useSignUp();
  const navigate = useNavigate();

  const stepAtom = useMemo(() => Atom.make<SignUpStep>("form"), []);
  const emailAtom = useMemo(() => Atom.make(""), []);
  const passwordAtom = useMemo(() => Atom.make(""), []);
  const codeAtom = useMemo(() => Atom.make(""), []);
  const errorAtom = useMemo(() => Atom.make<string | null>(null), []);
  const busyAtom = useMemo(() => Atom.make(false), []);

  const step = useAtomValue(stepAtom);
  const setStep = useAtomSet(stepAtom);
  const email = useAtomValue(emailAtom);
  const setEmail = useAtomSet(emailAtom);
  const password = useAtomValue(passwordAtom);
  const setPassword = useAtomSet(passwordAtom);
  const code = useAtomValue(codeAtom);
  const setCode = useAtomSet(codeAtom);
  const error = useAtomValue(errorAtom);
  const setError = useAtomSet(errorAtom);
  const busy = useAtomValue(busyAtom);
  const setBusy = useAtomSet(busyAtom);

  const runStart = useAtomSet(startSignUpFn);
  const runVerify = useAtomSet(verifySignUpCodeFn);
  const runGoogle = useAtomSet(startGoogleSignUpFn);

  const deps: SignUpDoorDeps | null = clerk.isLoaded
    ? {
      navigate,
      search,
      set: { busy: setBusy, error: setError, step: setStep },
      setActive: clerk.setActive,
      signUp: clerk.signUp,
    }
    : null;
  const disabled = deps === null || busy;

  const submitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (deps !== null) runStart({ deps, email, password });
  };
  const submitCode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (deps !== null) runVerify({ code, deps });
  };
  const back = () => {
    setError(null);
    setCode("");
    setStep("form");
  };

  return (
    <Door
      error={error === null ? undefined : error}
      footer={
        <>
          Already have an account? <a href="/orca/login">Sign in</a>
        </>
      }
      subtitle={step === "code" ? `We sent a six-digit code to ${email}` : undefined}
      title="Create your Orcaloops account"
    >
      {step === "form"
        ? (
          <form className="ol-door-body" data-testid="sign-up-form" onSubmit={submitForm}>
            <Field label="Email">
              <Input
                autoComplete="email"
                disabled={disabled}
                name="emailAddress"
                onChange={setEmail}
                type="email"
                value={email}
              />
            </Field>
            <Field label="Password">
              <Input
                autoComplete="new-password"
                disabled={disabled}
                name="password"
                onChange={setPassword}
                type="password"
                value={password}
              />
            </Field>
            <div id="clerk-captcha" />
            <button className="ol-btn ol-fill" data-testid="sign-up-continue" disabled={disabled} type="submit">
              Create account
            </button>
            <div className="ol-or">or</div>
            <OAuthButton
              disabled={disabled}
              onClick={() => {
                if (deps !== null) runGoogle(deps);
              }}
              provider="google"
              testId="sign-up-google"
            />
          </form>
        )
        : (
          <form className="ol-door-body" data-testid="sign-up-code-form" onSubmit={submitCode}>
            <Field label="Code">
              <Input
                autoComplete="one-time-code"
                autoFocus
                disabled={disabled}
                inputMode="numeric"
                name="code"
                onChange={setCode}
                value={code}
              />
            </Field>
            <button className="ol-btn ol-fill" data-testid="sign-up-verify" disabled={disabled} type="submit">
              Verify
            </button>
            <Btn disabled={busy} onClick={back} testId="sign-up-back">Back</Btn>
          </form>
        )}
    </Door>
  );
}

/**
 * The whole `/orca/sign-up` page: the bar-only frame, one `<main>`, the door.
 *
 * @since 1.0.0
 */
export function SignUpPage({ search }: { readonly search: LoginSearch }): ReactNode {
  return (
    <OrcaloopsFrame
      top={
        <>
          <TopMark />
          <TopCrumbs crumbs={[{ label: "Create account" }]} />
          <TopGrow />
          <ThemeToggle />
        </>
      }
    >
      <main style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <SignUpDoor search={search} />
      </main>
    </OrcaloopsFrame>
  );
}
